/**
 * usePeerLatency — round-trip time on the live WebRTC connections, polled from the browser.
 *
 * No extra traffic and no server involved: `RTCPeerConnection.getStats()` already measures the
 * path the video is taking, so turning the readout on costs a stats read rather than a ping.
 * That is what makes it safe to leave on over a wall of twenty players.
 *
 * Generic over WHERE the connections come from, because the two products differ: the cloud holds
 * one peer connection per node (its NodeConnectionManager), the local app holds exactly one to
 * its own SFU. Both hand this hook a list of `{ key, pc }` and read the answer back by key.
 *
 * RTT is a property of the CONNECTION, not of a camera — every camera streaming over one peer
 * connection shares one figure, and saying so is the point: it tells an operator the transport is
 * slow rather than blaming a camera.
 *
 * Disabled clears the map, so switching the overlay off can't leave a stale number on screen.
 */
import { useEffect, useRef, useState } from 'react';

export interface PeerLatency {
  /** Round-trip time in ms, from the succeeded ICE candidate pair. */
  rtt: number;
  /** Inbound video jitter in ms. */
  jitter: number;
  /** Cumulative inbound video packets lost since the connection opened, summed over every
   *  video stream on it. Never goes down — do not read it as a rate. */
  packetsLost: number;
  /** Inbound video loss over the last poll interval, in percent (0-100), summed over every
   *  video stream on the connection. This is the figure that says whether video is being
   *  lost NOW. */
  lossPct: number;
}

export interface LatencyPeer {
  key: string;
  pc: RTCPeerConnection | null | undefined;
}

const POLL_MS = 2000;

export function usePeerLatency(
  getPeers: () => LatencyPeer[],
  enabled: boolean,
): Map<string, PeerLatency> {
  const [latency, setLatency] = useState<Map<string, PeerLatency>>(new Map());
  // Per connection: the totals at the previous poll, for the per-interval loss rate.
  const prevTotals = useRef<Map<string, { lost: number; received: number }>>(new Map());

  useEffect(() => {
    if (!enabled) {
      // A badge that disappears is honest; a frozen figure is not.
      setLatency((prev) => (prev.size ? new Map() : prev));
      prevTotals.current.clear();
      return;
    }
    let cancelled = false;

    const poll = async () => {
      const peers = getPeers();
      if (cancelled || peers.length === 0) return;

      const next = new Map<string, PeerLatency>();
      for (const { key, pc } of peers) {
        if (!pc) continue;
        try {
          const stats = await pc.getStats();
          // One connection carries every tile, so there is one inbound-rtp report PER
          // camera. Sum them all — this used to keep whichever report came last (and none
          // at all if they came before the candidate pair), so the badge showed one random
          // tile's lifetime total.
          let rtt = -1; // -1 until the selected candidate pair is seen
          let lost = 0;
          let received = 0;
          let jitterMax = 0;
          stats.forEach((report: any) => {
            // The SELECTED candidate pair is the one carrying media; the others are rejected
            // alternatives whose RTT means nothing here.
            if (report.type === 'candidate-pair' && report.state === 'succeeded') {
              rtt = Math.round((report.currentRoundTripTime || 0) * 1000);
            }
            if (report.type === 'inbound-rtp' && report.kind === 'video') {
              lost += Math.max(0, report.packetsLost || 0);
              received += report.packetsReceived || 0;
              jitterMax = Math.max(jitterMax, report.jitter || 0);
            }
          });
          if (rtt < 0) continue;
          // Loss over the last interval. A new tile adds its own lifetime counters, and a
          // closed one takes them away, so clamp: a negative delta just means "no new loss".
          const prev = prevTotals.current.get(key);
          const dLost = prev ? Math.max(0, lost - prev.lost) : 0;
          const dRecv = prev ? Math.max(0, received - prev.received) : 0;
          prevTotals.current.set(key, { lost, received });
          next.set(key, {
            rtt,
            jitter: Math.round(jitterMax * 1000),
            packetsLost: lost,
            lossPct: dLost + dRecv > 0 ? Math.round((1000 * dLost) / (dLost + dRecv)) / 10 : 0,
          });
        } catch {
          /* the connection may be closing mid-poll — skip it this round */
        }
      }
      if (!cancelled) setLatency(next);
    };

    poll();
    const interval = setInterval(poll, POLL_MS);
    return () => { cancelled = true; clearInterval(interval); };
    // `getPeers` is a getter each caller keeps stable; depending on it would restart the poll
    // on every render.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [enabled]);

  return latency;
}
